import { AlertTriangle, RefreshCw } from "lucide-react";
import { useCategories } from "@/features/catalog/hooks";
import { CategoryTile } from "@/components/catalog/CategoryTile";
import { CategoryTileSkeleton } from "@/components/catalog/Skeletons";
import { SubmitButton } from "@/components/form/SubmitButton";

/**
 * Categories index page — a rich grid of every catalog category with
 * an icon tile, name and subtitle.
 */
export default function Categories() {
    const { data, isLoading, isError, refetch } = useCategories();

    return (
        <div data-testid="categories-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <h1 className="tsgo-h1">All categories</h1>
                <p className="tsgo-lead mt-2">
                    Browse everything TSGo delivers — from fresh produce to pet care.
                </p>
            </section>

            <section className="tsgo-container mt-8">
                {isLoading ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {Array.from({ length: 12 }).map((_, i) => (
                            <CategoryTileSkeleton key={i} />
                        ))}
                    </div>
                ) : isError ? (
                    <ErrorState onRetry={() => refetch()} />
                ) : (
                    <div
                        data-testid="categories-grid"
                        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                    >
                        {data?.map((c) => (
                            <CategoryTile key={c.id} category={c} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-dashed border-error/40 bg-error/5 p-8 text-center">
            <AlertTriangle className="mb-3 h-8 w-8 text-error" />
            <p className="font-semibold text-foreground">Couldn't load categories</p>
            <p className="mt-1 text-sm text-muted-foreground">
                Please check your connection and try again.
            </p>
            <div className="mt-4">
                <SubmitButton
                    type="button"
                    onClick={onRetry}
                    variant="outline"
                    fullWidth={false}
                    data-testid="categories-retry"
                >
                    <RefreshCw className="mr-1.5 h-4 w-4" /> Retry
                </SubmitButton>
            </div>
        </div>
    );
}
